
import React from "react";
import { useForm } from 'react-hook-form';
import RFG from "../../libs/inputs/RFG";
import { Box, Typography, Button, Grid, Stack } from "@mui/material";
import { FormHeroHeader, FormSectionCard, FormFooter, labelStyle } from "../extra/FormLayout";
import { BsLayersFill, BsPlusCircleFill } from "react-icons/bs";

export default function ServiceTaskForm({
    title,
    tasks = [],
    service = {},
    defaultValues = {},
    onSubmit = (data) => { },
    onChange = (column, data) => { },
    back = () => { } }) {

    const initialChecklist = React.useMemo(() => {
        if (!defaultValues?.checklist) return [];
        if (Array.isArray(defaultValues.checklist)) {
            return defaultValues.checklist.map(item => typeof item === 'object' ? (item?.label || '') : String(item));
        }
        return [];
    }, [defaultValues]);

    const [checklist, setChecklist] = React.useState(initialChecklist);

    React.useEffect(() => {
        setChecklist(initialChecklist);
    }, [initialChecklist]);

    const { handleSubmit, control, formState: { errors } } = useForm({
        defaultValues: defaultValues,
        mode: "all"
    });

    const handleChecklistChange = (index, value) => {
        setChecklist(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const handleAddItem = () => {
        setChecklist(prev => [...prev, ""]);
    };

    const handleRemoveItem = (index) => {
        setChecklist(prev => prev.filter((_, i) => i !== index));
    };

    const onFormSubmit = (data) => {
        const cleanedChecklist = checklist
            .map(item => (typeof item === 'string' ? item.trim() : ''))
            .filter(Boolean);

        onSubmit({
            ...data,
            checklist: cleanedChecklist
        });
    };

    return (
        <Box sx={{ py: 3, px: { xs: 1, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
            <FormHeroHeader
                icon={<BsLayersFill size={26} />}
                title={title || `Add Tasks to ${service.service_name || 'Service'}`}
                subtitle="Define task requirements, set continuity rules, and create custom checklists."
                badgeText="Service Task"
            />
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <FormSectionCard
                    icon={<BsLayersFill size={20} />}
                    title="Task Configuration & Checklist Items"
                    subtitle="Specify task name, continuity dependencies, and checklist steps"
                >
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Stack spacing={2.5}>
                                <RFG name="task_name" label="Task Name *" control={control} labelStyle={labelStyle} rules={{
                                    required: true, maxLength: {
                                        value: 200,
                                        message: `Cannot exceed 200 characters`,
                                    }
                                }}>
                                    <input autoComplete="false" placeholder="e.g. Server Configuration & Setup" />
                                </RFG>

                                <RFG name="continuity" label="Continuity of this Task" control={control} labelStyle={labelStyle}>
                                    <select>
                                        <option value="">Select Preceding Task</option>
                                        {tasks?.filter(t => t.id !== defaultValues?.id).map((company) => (
                                            <option key={company.id} value={company.id}>{company.task_name}</option>
                                        ))}
                                    </select>
                                </RFG>
                                <RFG name="description" label="Description" control={control} labelStyle={labelStyle} rules={{
                                    maxLength: {
                                        value: 1000,
                                        message: `Cannot exceed 1000 characters`,
                                    }
                                }}>
                                    <textarea style={{ height: 108 }} autoComplete="false" placeholder="Provide detailed instructions and scope..." rows={4} />
                                </RFG>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 2, bgcolor: '#f8fafc', height: '100%' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} pb={1.5} borderBottom="1px solid #e2e8f0">
                                    <Typography fontSize={14} fontWeight={700} color="#0f172a">Checklist Steps</Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<BsPlusCircleFill />}
                                        onClick={handleAddItem}
                                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 12 }}
                                    >
                                        Add Item ({checklist.length})
                                    </Button>
                                </Box>
                                <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
                                    {checklist?.map((itemText, idx) => (
                                        <Box key={idx} display={'flex'} alignItems={"center"} gap={1.5} sx={{ mb: 1.5, p: 1.2, bgcolor: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                                            <Box sx={{ width: '28px', height: '28px', borderRadius: '50%', bgcolor: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                                                {idx + 1}
                                            </Box>
                                            <Box width={1}>
                                                <input
                                                    type="text"
                                                    value={itemText}
                                                    onChange={(e) => handleChecklistChange(idx, e.target.value)}
                                                    placeholder={`Enter step ${idx + 1} description...`}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #cbd5e1',
                                                        fontSize: '13px',
                                                        outline: 'none',
                                                        color: '#1e293b'
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                onClick={() => handleRemoveItem(idx)}
                                                sx={{ cursor: 'pointer', userSelect: 'none', p: 1, color: '#ef4444', '&:hover': { bgcolor: '#fee2e2', borderRadius: '6px' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                title="Remove Item"
                                            >
                                                ❌
                                            </Box>
                                        </Box>
                                    ))}
                                    {checklist.length === 0 && (
                                        <Box sx={{ borderRadius: 2, p: 4, textAlign: 'center', bgcolor: '#ffffff', border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                                            No checklist items added. Click "Add Item" to create steps.
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </FormSectionCard>

                <FormFooter 
                    onCancel={() => back()} 
                    backText="Back" 
                    submitText={defaultValues?.id ? "Update Task" : "Save Task"} 
                    isEdit={!!defaultValues?.id}
                    backTestId="back-servicetask-btn" 
                    saveTestId="save-servicetask-btn" 
                />
            </form>
        </Box>
    );
}